import type { IInputRadioPropsWithoutModelValue } from '@/components';
import { reactive, ref, type Ref } from 'vue';
import { DropdownItemBase } from './DropdownItemBase';

/**
 * The type we use in the create method.
 * Here we allow the value to either be a ref or just a TValue so its not frustrating to use the dropdown
 */
type IDropdownItemRadio<TValue> = {
    [K in keyof Omit<IInputRadioPropsWithoutModelValue<TValue>, 'val'>]: undefined extends IInputRadioPropsWithoutModelValue<TValue>[K]
        ? IInputRadioPropsWithoutModelValue<TValue>[K] | Ref<Exclude<IInputRadioPropsWithoutModelValue<TValue>[K], undefined>> | undefined
        : IInputRadioPropsWithoutModelValue<TValue>[K] | Ref<Exclude<IInputRadioPropsWithoutModelValue<TValue>[K], undefined>>;
} & {
    value: TValue | Ref<TValue>; // This must a ref in the type since reactive unrefs it, and then the type is UnwrapRef<TValue> which does not fit in a TValue
};

/**
 * The type we use internally in the constructor.
 * This has to have the value as a Ref, otherwise Typescript will be confused.
 * The problem is that when we make a reactive object, it unwraps the refs, and then the type is UnwrapRef<TValue> which does not fit in a TValue
 * This seems like a bug in Vue, so there is some extra stuff here to make the everything happy.
 */
type IDropdownItemRadioInternal<TValue> = {
    [K in keyof Omit<IInputRadioPropsWithoutModelValue<TValue>, 'val'>]: undefined extends IInputRadioPropsWithoutModelValue<TValue>[K]
        ? IInputRadioPropsWithoutModelValue<TValue>[K] | Ref<Exclude<IInputRadioPropsWithoutModelValue<TValue>[K], undefined>> | undefined
        : IInputRadioPropsWithoutModelValue<TValue>[K] | Ref<Exclude<IInputRadioPropsWithoutModelValue<TValue>[K], undefined>>;
} & {
    value: Ref<TValue>; // This must a ref in the type since reactive unrefs it, and then the type is UnwrapRef<TValue> which does not fit in a TValue
};

export class DropdownItemRadio<TValue> extends DropdownItemBase {
    modelValue: Ref<TValue>;

    props: IInputRadioPropsWithoutModelValue<TValue>;

    get uniqueLoopKey() {
        return `${this.props.id}-${this.props.translationkey}-${this.props.name}-${this.props.labelclass}`;
    }

    /**
     * This is here so a pojo wont fit the class, and thus we get build errors if we havent used the create method
     */
    private abuseBlocker: boolean = false;

    private constructor(modelValue: Ref<TValue>, props: IDropdownItemRadioInternal<TValue>, hideDropDownItem?: Ref<boolean> | undefined) {
        super();
        this.modelValue = modelValue;
        this.hideDropDownItem = hideDropDownItem;

        this.props = reactive(props);
    }

    public static create<TValue>(
        options: { modelValue: Ref<TValue> } & IDropdownItemRadio<TValue> & Pick<DropdownItemBase, 'hideDropDownItem'>,
    ): DropdownItemRadio<TValue> {
        const { modelValue, hideDropDownItem, value, ...props } = options;
        return new DropdownItemRadio(modelValue, { value: ref(value), ...props }, hideDropDownItem);
    }
}
